// Reads physical cards from the guesser's own camera. It runs entirely in the browser.
//
// Paper colours shift a lot with room lighting, so fixed hue ranges are unreliable. Instead
// you show each card once to calibrate, in your actual lighting, and each frame is matched
// against those samples in CIELAB, a colour space where distance tracks perceived difference.

import type { ColorId } from "../../shared/colors.ts";

export type Lab = [number, number, number];
export type Calibration = Partial<Record<ColorId, Lab>>;

const STORE = "ml_cv_calibration";

export function loadCalibration(): Calibration {
  try { return JSON.parse(localStorage.getItem(STORE) ?? "{}"); } catch { return {}; }
}
export function saveCalibration(c: Calibration): void {
  localStorage.setItem(STORE, JSON.stringify(c));
}

export function rgbToLab(r: number, g: number, b: number): Lab {
  const lin = (v: number) => { const s = v / 255; return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  const R = lin(r), G = lin(g), B = lin(b);
  const x = (0.4124 * R + 0.3576 * G + 0.1805 * B) / 0.95047;
  const y = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  const z = (0.0193 * R + 0.1192 * G + 0.9505 * B) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(x), fy = f(y), fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/** Distance weighted toward chroma, because brightness swings most as a card tilts. */
function dist(a: Lab, b: Lab): number {
  const dL = (a[0] - b[0]) * 0.5, da = a[1] - b[1], db = a[2] - b[2];
  return Math.sqrt(dL * dL + da * da + db * db);
}

export interface FrameResult { color: ColorId | null; share: number }

/** Classifies the central region of an RGBA frame. */
export function classifyPixels(rgba: Uint8ClampedArray, cal: Calibration, maxDist = 24): FrameResult {
  const ids = Object.keys(cal) as ColorId[];
  if (!ids.length) return { color: null, share: 0 };
  const counts = new Map<ColorId, number>();
  let total = 0;
  for (let i = 0; i < rgba.length; i += 4) {
    total++;
    const lab = rgbToLab(rgba[i]!, rgba[i + 1]!, rgba[i + 2]!);
    let best: ColorId | null = null, bestD = maxDist;
    for (const id of ids) {
      const d = dist(lab, cal[id]!);
      if (d < bestD) { bestD = d; best = id; }
    }
    if (best) counts.set(best, (counts.get(best) ?? 0) + 1);
  }
  let top: ColorId | null = null, topN = 0;
  for (const [id, n] of counts) if (n > topN) { top = id; topN = n; }
  return { color: top, share: total ? topN / total : 0 };
}

/**
 * Debounces frames into events. The same colour across several frames fires a detection.
 * Then the watcher disarms until the card leaves, so a held card can't open a second trial.
 */
export class CardWatcher {
  private streak = 0;
  private current: ColorId | null = null;
  private armed = true;
  private clearFrames = 0;

  constructor(private needFrames = 4, private minShare = 0.4, private clearNeed = 4) {}

  rearm(): void { this.armed = false; }

  push(r: FrameResult): { color: ColorId; confidence: number } | null {
    const present = r.color !== null && r.share >= this.minShare;
    if (!present) {
      this.streak = 0;
      this.current = null;
      if (!this.armed && ++this.clearFrames >= this.clearNeed) { this.armed = true; this.clearFrames = 0; }
      return null;
    }
    this.clearFrames = 0;
    if (r.color === this.current) this.streak++;
    else { this.current = r.color; this.streak = 1; }
    if (this.armed && this.streak >= this.needFrames) {
      this.armed = false;
      return { color: r.color!, confidence: Math.min(1, r.share) };
    }
    return null;
  }
}

const SIZE = 48;

/** Samples the middle of the video into a small canvas. Returns null until video is flowing. */
export function sampleVideo(video: HTMLVideoElement, canvas: HTMLCanvasElement): Uint8ClampedArray | null {
  const w = video.videoWidth, h = video.videoHeight;
  if (!w || !h) return null;
  canvas.width = SIZE; canvas.height = SIZE;
  const c = canvas.getContext("2d", { willReadFrequently: true });
  if (!c) return null;
  const side = Math.min(w, h) * 0.5;
  c.drawImage(video, (w - side) / 2, (h - side) / 2, side, side, 0, 0, SIZE, SIZE);
  return c.getImageData(0, 0, SIZE, SIZE).data;
}

export function meanLab(rgba: Uint8ClampedArray): Lab {
  let l = 0, a = 0, b = 0, n = 0;
  for (let i = 0; i < rgba.length; i += 4) {
    const v = rgbToLab(rgba[i]!, rgba[i + 1]!, rgba[i + 2]!);
    l += v[0]; a += v[1]; b += v[2]; n++;
  }
  return [l / n, a / n, b / n];
}
